/* ============================================================================
   jr-xsell — Módulo de recomendados en carrito lateral (drawer)
   Feature: 001-cart-drawer-xsell · Tareas: T012 + T017b + T018 + T023 + T025 + T027 + T028 + T029 + T033 + T034 + T036

   Resuelve el **ciclo de vida y diferimiento**, la vía curada primaria, el
   respaldo natural y la hidratación del selector SSR. T027 acepta el vocabulario
   SSR cerrado `top|bottom|accesorio` derivado por `CartProfile`. T028 separa la
   cantidad visible del límite del pool curado: valida el `candidate-limit=10`
   emitido por SSR y lo usa solo en la petición. T029 consume una única ancla
   alternativa SSR, solo tras un `none` curado conforme de la primaria y antes
   del respaldo. El perfil continúa derivándose exclusivamente en la sección.
   T036 corrige y verifica la reserva dinámica basada en la mayor altura real.

   Satisface: FR-007, FR-007a, FR-007b, FR-012..FR-016, FR-022, FR-023,
   FR-024, D5, D8 y D11. Prefijos obligatorios `jr-xsell-`/`JRXsell` (Principio II).

   ---------------------------------------------------------------------------
   POR QUÉ EL RECÁLCULO NO SE SUSCRIBE A `cart-update`   (V0-4 + V0-5)
   ---------------------------------------------------------------------------
   Los renders de `#CartDrawer` y `.drawer__inner` destruyen y recrean el módulo;
   el `connectedCallback` de la instancia nueva dispara su recálculo. La ruta
   parcial de `CartItems.onCartUpdate`, en cambio, sustituye solo
   `cart-drawer-items` y `.cart-drawer__footer`, por lo que T033 observa
   declarativamente esas dos sustituciones y recalcula la instancia
   superviviente. Suscribirse ADEMÁS a `cart-update` provocaría peticiones
   duplicadas. Ver `contracts/module-component.md` y `contracts/cart-ajax.md`.

   ---------------------------------------------------------------------------
   DETECCIÓN DE APERTURA DEL DRAWER   (el problema central de T012)
   ---------------------------------------------------------------------------
   `connectedCallback` también corre en la CARGA INICIAL de la página, porque el
   drawer existe en el DOM aunque esté cerrado. Pedir ahí rompería la carga
   diferida: habría una petición de recomendaciones en CADA pageview aunque el
   cliente nunca abra el carrito (FR-023 / D5).

   Dawn **no publica ningún evento de apertura** (verificado: `assets/cart-drawer.js`
   no contiene `publish`, `dispatchEvent` ni `CustomEvent`). La única señal
   observable es la clase `active` que `open()` añade al propio `<cart-drawer>`
   (`cart-drawer.js:34`) y que `close()` retira (`:53`). Por eso el diferimiento
   usa un **MutationObserver sobre `<cart-drawer>` filtrando `class`**.

   Alternativas descartadas, con evidencia:
     · click en `#cart-icon-bubble` — duplica el binding de Dawn y PIERDE las
       aperturas programáticas (`renderContents()` llama a `open()`, `:88`).
     · parchear `CartDrawer.prototype.open` — altera el comportamiento de Dawn
       en runtime; contra el espíritu del Principio I.
     · `transitionend` — el propio Dawn comenta que la animación "doesn't seem
       to always get triggered" (`cart-drawer.js:32-33`): poco fiable.
     · IntersectionObserver — el ocultamiento es `visibility: hidden`, que IO no
       rastrea sin `trackVisibility` (soporte limitado).

   Además, `connectedCallback` comprueba PRIMERO y de forma síncrona si el drawer
   ya está abierto: es el caso de un elemento recreado mientras el drawer está
   abierto, donde no hay que esperar a ninguna mutación de clase.

   ---------------------------------------------------------------------------
   QUIÉN EMITE CADA ESTADO   (vocabulario cerrado, contracts/module-component.md)
   ---------------------------------------------------------------------------
     · `idle`          — lo emite el SERVIDOR en el markup de montaje (aún no se
                         ha buscado nada). Es el estado en reposo.
     · `loading`       — lo pone ESTE JS al iniciar la PRIMERA búsqueda.
     · `recalculating` — lo pone ESTE JS al iniciar una búsqueda posterior, es
                         decir cuando el drawer ya mostró una sugerencia antes.
     · `ready`         — lo pone este JS al aplicar una sección cuyo
                         `data-jr-xsell-result` vale `suggestion`.
     · `empty`         — lo pone este JS SOLO cuando se agota la cadena de
                         fuentes (colapsa sin hueco, FR-003).

   La SECCIÓN nunca emite estados: es un portador de un fetch y solo conoce el
   resultado de su propio render (`data-jr-xsell-result` = suggestion | none),
   no si quedan fuentes. Si emitiera `empty` y este JS lo copiara, el módulo
   colapsaría a cero en mitad de la cadena y se expandiría al llegar la fuente
   siguiente: el CLS que la opción C evita. Ver la tabla de decisión en
   contracts/module-component.md.

   El SERVIDOR **no puede** emitir `recalculating`: renderiza sin estado y sólo
   conoce `cart`; "carrito no vacío" no equivale a "ya mostró una sugerencia".
   Por eso la persistencia vive en el ancestro descrito abajo, y
   `recalculating` queda como estado SEMÁNTICO (y portador de `aria-busy`): la
   ALTURA durante una recreación la resuelve el CSS usando la mayor altura real
   que este JS haya persistido en el drawer.

   ---------------------------------------------------------------------------
   PERSISTENCIA ENTRE RECREACIONES — `data-jr-xsell-shown` en `<cart-drawer>`
   ---------------------------------------------------------------------------
   El elemento se destruye en los renders completos, así que no puede conservar
   por sí mismo el historial entre instancias. `<cart-drawer>` en cambio
   SOBREVIVE a todas las vías de re-render (Dawn reemplaza el `innerHTML` de
   `.drawer__inner` o de `#CartDrawer`, ambos por dentro de `<cart-drawer>`).
   Por eso, la primera vez que se muestra una sugerencia se marca el ancestro
   con `data-jr-xsell-shown="true"` y cada sugerencia aplicada eleva allí una
   reserva dinámica al máximo medido hasta ese momento.

   El marcador y esa reserva los consume una regla CSS en el PRIMER PAINT del
   subárbol re-renderizado, sin que la instancia nueva tenga que medir antes de
   reservar espacio. Ver `assets/jr-xsell-cart-recommendations.css`.

   ---------------------------------------------------------------------------
   CARGA DEL ASSET   (dependencia hacia T013/T014, no se resuelve aquí)
   ---------------------------------------------------------------------------
   Este archivo debe cargarse como **script clásico con `defer`, DESPUÉS de
   `pubsub.js`**, para compartir el ámbito global de `publish`/`PUB_SUB_EVENTS`
   (`layout/theme.liquid:32-34` los carga así). T018 usa estas APIs para publicar
   `cart-update` después del agregado; el orden existente, fijado por el snippet
   de montaje, permite acceder a ambas globales.
   ============================================================================ */

(function () {
  'use strict';

  /** Vocabulario CERRADO de estados. No añadir valores sin actualizar el contrato. */
  const JR_XSELL_STATE = {
    idle: 'idle',
    loading: 'loading',
    ready: 'ready',
    recalculating: 'recalculating',
    empty: 'empty',
  };

  /**
   * Resultado de UNA fuente, leído del raíz que devuelve la sección. Eje
   * INDEPENDIENTE del estado: la sección solo sabe qué encontró en su propio
   * render, no si quedan fuentes por consultar (eso lo decide el JS).
   */
  const JR_XSELL_RESULT = {
    suggestion: 'suggestion',
    none: 'none',
  };

  const JR_XSELL_ORIGIN = {
    curated: 'curada',
    fallback: 'respaldo',
  };

  const JR_XSELL_STATE_ATTR = 'data-jr-xsell-state';
  const JR_XSELL_RESULT_ATTR = 'data-jr-xsell-result';
  const JR_XSELL_ORIGIN_ATTR = 'data-jr-xsell-origin';
  const JR_XSELL_SHOWN_ATTR = 'data-jr-xsell-shown';
  const JR_XSELL_RESERVED_HEIGHT_PROPERTY = '--jr-xsell-reserved-height';
  const JR_XSELL_RECOMMENDATIONS_URL_ATTR = 'data-jr-xsell-recommendations-url';
  const JR_XSELL_ANCHOR_PRODUCT_ID_ATTR = 'data-jr-xsell-anchor-product-id';
  const JR_XSELL_ALTERNATE_ANCHOR_PRODUCT_ID_ATTR =
    'data-jr-xsell-alternate-anchor-product-id';
  const JR_XSELL_COLLECTION_BASE_ATTR = 'data-jr-xsell-collection-base';
  const JR_XSELL_FALLBACK_POSITION_ATTR = 'data-jr-xsell-fallback-position';
  const JR_XSELL_FALLBACK_GENDER_ATTR = 'data-jr-xsell-fallback-gender';
  const JR_XSELL_CANDIDATE_LIMIT_ATTR = 'data-jr-xsell-candidate-limit';
  const JR_XSELL_SURFACE_ATTR = 'data-jr-xsell-surface';
  const JR_XSELL_SECTION_ID_ATTR = 'data-jr-xsell-section-id';
  const JR_XSELL_CART_URL_ATTR = 'data-jr-xsell-cart-url';
  const JR_XSELL_VARIANT_ID_ATTR = 'data-jr-xsell-variant-id';
  const JR_XSELL_ELEMENT_NAME = 'jr-xsell-cart-recommendations';
  const JR_XSELL_DRAWER_SECTION_ID = 'jr-xsell-cart-recommendations';
  const JR_XSELL_CART_SECTION_ID = 'jr-xsell-cart-page-recommendations';
  const JR_XSELL_SUBMIT_SELECTOR = '.jr-xsell-suggestion-card__submit';
  const JR_XSELL_CARD_SELECTOR = '.jr-xsell-suggestion-card';
  const JR_XSELL_CARD_ERROR_SELECTOR = '.jr-xsell-suggestion-card__error';
  const JR_XSELL_PRICE_SELECTOR = '.jr-xsell-suggestion-card__price';
  const JR_XSELL_PICKER_SELECTOR = '[data-jr-xsell-variant-picker]';
  const JR_XSELL_MATRIX_SELECTOR = '[data-jr-xsell-variant-matrix]';
  const JR_XSELL_OPTION_SELECTOR = '[data-jr-xsell-option-position]';
  const JR_XSELL_OPTION_SELECT_SELECTOR = 'select[data-jr-xsell-option-select]';
  const JR_XSELL_CART_UPDATE_SOURCE = 'jr-xsell-cart-recommendations';
  const JR_XSELL_CURATED_CANDIDATE_LIMIT = '10';
  const JR_XSELL_CURATED_OUTCOME = {
    suggestion: 'suggestion',
    none: 'none',
    failure: 'failure',
    interrupted: 'interrupted',
  };
  const JR_XSELL_FALLBACK_POSITIONS = new Set(['top', 'bottom', 'accesorio']);
  const JR_XSELL_FALLBACK_GENDERS = new Set(['hombre', 'mujer']);
  const JR_XSELL_DRAWER_ITEMS_SELECTOR = 'cart-drawer-items';
  const JR_XSELL_DRAWER_FOOTER_SELECTOR = '.cart-drawer__footer';
  const JR_XSELL_CART_ITEMS_CONTAINER_SELECTOR = '#main-cart-items .js-contents';
  const JR_XSELL_DAWN_CART_ITEM_SELECTOR = '.cart-item';
  const JR_XSELL_CART_LINE_SELECTOR = '[data-jr-xsell-product-id]';
  const JR_XSELL_PRODUCT_ID_ATTR = 'data-jr-xsell-product-id';
  const JR_XSELL_QUANTITY_INPUT_SELECTOR =
    'input[name="updates[]"][data-quantity-variant-id]';
  const JR_XSELL_GENERIC_ADD_ERROR =
    'No pudimos agregar este producto al carrito. Inténtalo nuevamente.';

  /** Elemento y clase de Dawn. Son los ÚNICOS nombres ajenos que se leen. */
  const JR_XSELL_DRAWER_TAG = 'cart-drawer';
  const JR_XSELL_DRAWER_OPEN_CLASS = 'active';

  class JRXsellCartRecommendations extends HTMLElement {
    abortController = null;
    drawerObserver = null;
    partialUpdateObserver = null;
    partialUpdateFrame = null;
    cartUpdateUnsubscriber = null;
    cartItemsObserver = null;
    lastCartUpdateSignature = null;
    cartSignature = null;
    cartHadLines = false;
    cartEmptyRecoveryStarted = false;
    hasRequested = false;
    addPending = false;
    variantMatrices = new WeakMap();

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.hydrateVariantPickers();

      if (this.isCartSurface) {
        this.observeCartItemsEmptyTransition();
        this.observeCartUpdates();
        if (this.hasValidSurfaceConfiguration() && this.hasCartAnchor) {
          this.requestSuggestion();
        } else {
          this.settleWithoutSuggestion();
        }
        return;
      }

      const cartSnapshot = this.readCartSnapshot();
      if (cartSnapshot) this.applyCartSnapshot(cartSnapshot);

      this.observePartialDrawerUpdates();

      // Caso "elemento recreado con el drawer abierto": no hay que esperar
      // ninguna mutación de clase, el drawer ya está visible.
      if (this.isSurfaceOpen) {
        this.requestSuggestion();
        return;
      }

      // Carga inicial (drawer cerrado): NO se pide nada. Se espera la apertura.
      this.observeDrawerOpen();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);

      // Un render completo destruye el elemento: una respuesta o un recálculo
      // diferido huérfanos intentarían escribir sobre un nodo que ya no existe.
      this.abortPendingRequest();
      this.stopObservingCartItemsEmptyTransition();
      this.stopObservingCartUpdates();
      this.stopObservingDrawer();
      this.stopObservingPartialDrawerUpdates();
    }

    /* --- Agregado al carrito --------------------------------------------- */

    handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const submitButton = event.target.closest(JR_XSELL_SUBMIT_SELECTOR);
      if (!submitButton || !this.contains(submitButton)) return;

      event.preventDefault();
      if (this.addPending || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

      this.addVariantToCart(submitButton);
    };

    /* --- Selector de variantes -------------------------------------------- */

    handleChange = (event) => {
      if (!(event.target instanceof Element) || !event.target.matches(JR_XSELL_OPTION_SELECT_SELECTOR)) return;

      const picker = event.target.closest(JR_XSELL_PICKER_SELECTOR);
      if (!picker || !this.contains(picker)) return;

      // El montaje cart vive dentro de <cart-items>; Dawn no debe interpretar
      // este select JR reconocido como un cambio de cantidad.
      event.stopPropagation();
      this.clearCardError(picker.closest(JR_XSELL_CARD_SELECTOR));
      this.hydrateVariantPicker(picker);
    };

    clearCardError(card) {
      if (!this.isConnected || !card?.isConnected || !this.contains(card)) return;

      const errorElement = card?.querySelector(JR_XSELL_CARD_ERROR_SELECTOR);
      if (errorElement?.isConnected && card.contains(errorElement)) {
        errorElement.textContent = '';
      }
    }

    hydrateVariantPickers() {
      this.querySelectorAll(JR_XSELL_PICKER_SELECTOR).forEach((picker) => {
        this.hydrateVariantPicker(picker);
      });
    }

    hydrateVariantPicker(picker) {
      const matrix = this.readVariantMatrix(picker);
      const card = picker.closest(JR_XSELL_CARD_SELECTOR);
      const submitButton = card?.querySelector(JR_XSELL_SUBMIT_SELECTOR);

      if (!matrix) {
        this.blockVariantSelection(submitButton);
        return;
      }

      this.updateOptionAvailability(picker, matrix);

      const selectedValues = this.readSelectedOptionValues(picker, matrix.options.length);
      const matchingVariants = selectedValues
        ? matrix.variants.filter((variant) =>
            variant.option_values.every((value, index) => value === selectedValues[index])
          )
        : [];
      const selectedVariant = matchingVariants.length === 1 ? matchingVariants[0] : null;

      if (!selectedVariant || selectedVariant.available !== true) {
        this.blockVariantSelection(submitButton);
        return;
      }

      if (submitButton) {
        submitButton.setAttribute(JR_XSELL_VARIANT_ID_ATTR, selectedVariant.variant_id);
        if (!this.addPending) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-disabled');
        }
      }

      const priceElement = card?.querySelector(JR_XSELL_PRICE_SELECTOR);
      if (priceElement) priceElement.textContent = selectedVariant.price_formatted;
    }

    readVariantMatrix(picker) {
      if (this.variantMatrices.has(picker)) return this.variantMatrices.get(picker);

      const matrixElement = picker.querySelector(JR_XSELL_MATRIX_SELECTOR);
      let matrix = null;

      try {
        matrix = matrixElement ? JSON.parse(matrixElement.textContent) : null;
      } catch (error) {
        console.warn('JRXsell: VariantMatrix SSR no es JSON válido.', error);
        this.variantMatrices.set(picker, null);
        return null;
      }

      if (!JRXsellCartRecommendations.isValidVariantMatrix(matrix)) {
        console.warn('JRXsell: VariantMatrix SSR ausente o inválida.');
        this.variantMatrices.set(picker, null);
        return null;
      }

      this.variantMatrices.set(picker, matrix);
      return matrix;
    }

    static isValidVariantMatrix(matrix) {
      if (!matrix || !Array.isArray(matrix.options) || !Array.isArray(matrix.variants)) return false;
      if (
        matrix.options.length === 0 ||
        !matrix.options.every((option) => typeof option === 'string' && option.trim().length > 0) ||
        matrix.variants.length === 0
      )
        return false;

      return matrix.variants.every(
        (variant) => {
          if (!variant || typeof variant !== 'object' || Array.isArray(variant)) return false;

          const variantId = variant.variant_id;
          const hasValidVariantId =
            (Number.isInteger(variantId) && variantId > 0) ||
            (typeof variantId === 'string' && /^\d+$/.test(variantId));

          return (
            hasValidVariantId &&
            Array.isArray(variant.option_values) &&
            variant.option_values.length === matrix.options.length &&
            variant.option_values.every((optionValue) => typeof optionValue === 'string') &&
            typeof variant.available === 'boolean' &&
            Number.isInteger(variant.price) &&
            variant.price >= 0 &&
            typeof variant.price_formatted === 'string' &&
            variant.price_formatted.trim().length > 0
          );
        }
      );
    }

    updateOptionAvailability(picker, matrix) {
      const optionGroups = Array.from(picker.querySelectorAll(JR_XSELL_OPTION_SELECTOR));
      const previousSelections = [];

      matrix.options.forEach((_option, optionIndex) => {
        const optionPosition = optionIndex + 1;
        const optionGroup = optionGroups.find(
          (group) => Number(group.getAttribute('data-jr-xsell-option-position')) === optionPosition
        );
        const select = optionGroup?.querySelector(JR_XSELL_OPTION_SELECT_SELECTOR);
        const options = select
          ? Array.from(select.options).filter((option) => option.value !== '')
          : [];

        options.forEach((option) => {
          const optionValue = option.getAttribute('data-jr-xsell-option-value');
          const hasAvailableVariant = matrix.variants.some(
            (variant) =>
              variant.available === true &&
              previousSelections.every(
                (selectedValue, previousIndex) =>
                  selectedValue === null || variant.option_values[previousIndex] === selectedValue
              ) &&
              variant.option_values[optionIndex] === optionValue
          );

          option.disabled = !hasAvailableVariant;
          if (hasAvailableVariant) {
            option.removeAttribute('aria-disabled');
          } else {
            option.setAttribute('aria-disabled', 'true');
            if (option.selected && select) select.value = '';
          }
        });

        const selectedOption = select?.selectedOptions[0];
        previousSelections.push(
          selectedOption && selectedOption.value !== '' && !selectedOption.disabled
            ? selectedOption.getAttribute('data-jr-xsell-option-value')
            : null
        );
      });
    }

    readSelectedOptionValues(picker, optionCount) {
      const optionGroups = Array.from(picker.querySelectorAll(JR_XSELL_OPTION_SELECTOR));
      const selectedValues = [];

      for (let optionPosition = 1; optionPosition <= optionCount; optionPosition += 1) {
        const optionGroup = optionGroups.find(
          (group) => Number(group.getAttribute('data-jr-xsell-option-position')) === optionPosition
        );
        if (!optionGroup) return null;

        const select = optionGroup.querySelector(JR_XSELL_OPTION_SELECT_SELECTOR);
        const selectedOption = select?.selectedOptions[0];
        if (!selectedOption || selectedOption.value === '' || selectedOption.disabled) return null;

        selectedValues.push(selectedOption.getAttribute('data-jr-xsell-option-value'));
      }

      return selectedValues;
    }

    blockVariantSelection(submitButton) {
      if (!submitButton) return;

      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.removeAttribute(JR_XSELL_VARIANT_ID_ATTR);
    }

    async addVariantToCart(submitButton) {
      const card = submitButton.closest(JR_XSELL_CARD_SELECTOR);
      const errorElement = card?.querySelector(JR_XSELL_CARD_ERROR_SELECTOR);

      if (
        !card ||
        !errorElement ||
        !this.isConnected ||
        !card.isConnected ||
        !submitButton.isConnected ||
        !this.contains(card)
      )
        return;

      const variantId = submitButton.getAttribute(JR_XSELL_VARIANT_ID_ATTR);
      const cartDrawer = this.drawer;
      const isCartSurface = this.isCartSurface;
      const wasDisabled = submitButton.disabled;
      const previousAriaDisabled = submitButton.getAttribute('aria-disabled');
      let failureMessage = JR_XSELL_GENERIC_ADD_ERROR;

      errorElement.textContent = '';
      this.addPending = true;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');

      try {
        if (!variantId) throw new Error('La tarjeta no incluye un variant ID SSR.');
        if (!isCartSurface && (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function')) {
          throw new Error('No se encontró un cart drawer compatible.');
        }
        if (!isCartSurface && typeof cartDrawer.renderContents !== 'function') {
          throw new Error('El cart drawer no expone renderContents().');
        }

        // En /cart la navegación es parte de la transacción: se valida antes
        // de construir el POST para que una configuración SSR inválida no
        // pueda modificar el carrito.
        const cartUrl = isCartSurface ? this.getValidatedCartUrl() : null;
        if (isCartSurface && !cartUrl) {
          throw new Error('La ruta SSR del carrito no es válida.');
        }

        const body = isCartSurface
          ? JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          : JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: cartDrawer.getSectionsToRender().map((section) => section.id),
              sections_url: window.location.pathname,
            });
        const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body });
        let cartData;

        try {
          cartData = await response.json();
        } catch {
          throw new Error('La respuesta de agregado no contiene JSON válido.');
        }

        if (!cartData || typeof cartData !== 'object' || Array.isArray(cartData)) {
          throw new Error('La respuesta de agregado no tiene el formato esperado.');
        }

        if (!response.ok || cartData.status || cartData.error) {
          failureMessage = JRXsellCartRecommendations.readShopifyAddError(cartData);
          throw new Error(`Shopify rechazó el agregado (HTTP ${response.status}).`);
        }

        if (isCartSurface) {
          this.addPending = false;
          window.location.assign(cartUrl);
          return;
        }

        // Otra mutación pudo destruir esta instancia mientras el POST estaba
        // pendiente. En ese caso no se escribe ni se renderiza desde el nodo huérfano.
        if (!this.isConnected || !cartDrawer.isConnected) return;

        this.addPending = false;
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: JR_XSELL_CART_UPDATE_SOURCE,
          productVariantId: variantId,
          cartData: cartData,
        }).catch((error) => {
          console.error('JRXsell: falló un suscriptor de cart-update.', error);
        });

        // A diferencia de la actualización parcial de CartItems, esta llamada
        // reemplaza #CartDrawer completo: destruye este módulo y el montaje
        // nuevo dispara exactamente un connectedCallback para recalcular.
        cartDrawer.renderContents(cartData);
      } catch (error) {
        console.error('JRXsell: no se pudo agregar la sugerencia al carrito.', error);
        this.addPending = false;

        if (
          !this.isConnected ||
          !card.isConnected ||
          !submitButton.isConnected ||
          !errorElement.isConnected ||
          !this.contains(card) ||
          !card.contains(submitButton) ||
          !card.contains(errorElement)
        )
          return;

        errorElement.textContent = failureMessage;
        submitButton.disabled = wasDisabled;
        if (previousAriaDisabled === null) {
          submitButton.removeAttribute('aria-disabled');
        } else {
          submitButton.setAttribute('aria-disabled', previousAriaDisabled);
        }
      }
    }

    static readShopifyAddError(cartData) {
      for (const field of ['description', 'message']) {
        const value = cartData[field];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
      }

      return JR_XSELL_GENERIC_ADD_ERROR;
    }

    getValidatedCartUrl() {
      const cartUrl = this.getAttribute(JR_XSELL_CART_URL_ATTR);
      if (!cartUrl) return null;

      try {
        const parsedUrl = new URL(cartUrl, window.location.origin);
        return parsedUrl.origin === window.location.origin && parsedUrl.pathname ? parsedUrl.toString() : null;
      } catch {
        return null;
      }
    }

    /* --- Superficie ------------------------------------------------------- */

    /** `<cart-drawer>` contenedor, o `null` si el módulo se monta en otra superficie. */
    get drawer() {
      return this.closest(JR_XSELL_DRAWER_TAG);
    }

    get surface() {
      return this.getAttribute(JR_XSELL_SURFACE_ATTR);
    }

    get isCartSurface() {
      return this.surface === 'cart';
    }

    get hasCartAnchor() {
      return Boolean(this.getAttribute(JR_XSELL_ANCHOR_PRODUCT_ID_ATTR));
    }

    get sectionId() {
      return this.getAttribute(JR_XSELL_SECTION_ID_ATTR);
    }

    get count() {
      return this.getAttribute('data-jr-xsell-count');
    }

    hasValidSurfaceConfiguration() {
      const expectedSectionId =
        this.surface === 'drawer'
          ? JR_XSELL_DRAWER_SECTION_ID
          : this.surface === 'cart'
            ? JR_XSELL_CART_SECTION_ID
            : null;
      const expectedCount = this.surface === 'drawer' ? '1' : this.surface === 'cart' ? '3' : null;
      const sectionId = this.sectionId;

      return (
        expectedSectionId !== null &&
        this.count === expectedCount &&
        this.getAttribute(JR_XSELL_CANDIDATE_LIMIT_ATTR) === JR_XSELL_CURATED_CANDIDATE_LIMIT &&
        sectionId === expectedSectionId &&
        /^[a-z0-9-]+$/.test(sectionId) &&
        (this.surface !== 'cart' || this.getValidatedCartUrl() !== null)
      );
    }

    /**
     * ¿La superficie está visible? En el drawer equivale a que Dawn le haya
     * puesto la clase `active`. Si NO hay drawer contenedor (superficie de la
     * futura página de carrito, D13) no hay nada que esperar: se considera
     * visible y el diferimiento no aplica.
     */
    get isSurfaceOpen() {
      const drawer = this.drawer;
      if (!drawer) return true;

      return drawer.classList.contains(JR_XSELL_DRAWER_OPEN_CLASS);
    }

    /** ¿Esta superficie ya mostró una sugerencia antes? (marcador de ancestro) */
    get hasShownBefore() {
      const drawer = this.drawer;

      return Boolean(drawer && drawer.hasAttribute(JR_XSELL_SHOWN_ATTR));
    }

    /* --- Diferimiento hasta la apertura ----------------------------------- */

    observeDrawerOpen() {
      const drawer = this.drawer;
      if (!drawer || this.drawerObserver) return;

      this.drawerObserver = new MutationObserver(() => {
        if (!this.isSurfaceOpen) return;

        // Una sola obtención por instancia: reabrir el drawer sin cambiar el
        // carrito NO debe alterar la sugerencia mostrada (FR-009).
        this.stopObservingDrawer();
        this.requestSuggestion();
      });

      this.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    stopObservingDrawer() {
      if (!this.drawerObserver) return;

      this.drawerObserver.disconnect();
      this.drawerObserver = null;
    }

    /* --- Recálculo exclusivo de /cart ----------------------------------- */

    observeCartItemsEmptyTransition() {
      if (!this.isCartSurface || this.cartItemsObserver) return;

      const cartItemsContainer = this.closest('cart-items')?.querySelector(
        JR_XSELL_CART_ITEMS_CONTAINER_SELECTOR
      );
      if (!cartItemsContainer) return;

      this.cartHadLines = cartItemsContainer.querySelectorAll(JR_XSELL_DAWN_CART_ITEM_SELECTOR).length > 0;
      this.cartItemsObserver = new MutationObserver(() => {
        if (this.cartEmptyRecoveryStarted) return;

        const currentLineCount = cartItemsContainer.querySelectorAll(
          JR_XSELL_DAWN_CART_ITEM_SELECTOR
        ).length;
        if (currentLineCount > 0) {
          this.cartHadLines = true;
          return;
        }
        if (!this.cartHadLines) return;

        // Dawn puede fallar antes de cart-update al vaciarse; se recarga el SSR vacío.
        this.cartEmptyRecoveryStarted = true;
        this.stopObservingCartItemsEmptyTransition();
        this.applyCartSnapshot({
          signature: '',
          anchorProductId: null,
          alternateAnchorProductId: null,
        });
        this.recalculateCartSuggestion(0);
        this.hasRequested = true;
        window.location.reload();
      });

      this.cartItemsObserver.observe(cartItemsContainer, { childList: true, subtree: true });
    }

    stopObservingCartItemsEmptyTransition() {
      if (!this.cartItemsObserver) return;

      this.cartItemsObserver.disconnect();
      this.cartItemsObserver = null;
    }

    observeCartUpdates() {
      if (!this.isCartSurface || this.cartUpdateUnsubscriber || typeof subscribe !== 'function') return;

      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (this.cartEmptyRecoveryStarted) return;

        // Dawn publica este contrato desde CartItems.updateQuantity. Los eventos
        // propios de Add drawer y cualquier otra fuente no son una entrada cart.
        if (!event || event.source !== 'cart-items') return;

        // El payload real de Dawn ya declara el carrito vacío; resuélvelo antes
        // de la firma/deduplicación para no conservar una recomendación obsoleta.
        if (event.cartData?.item_count === 0) {
          this.lastCartUpdateSignature = '';
          this.applyCartSnapshot({
            signature: '',
            anchorProductId: null,
            alternateAnchorProductId: null,
          });
          this.recalculateCartSuggestion(0);
          return;
        }

        const cartSnapshot = this.readCartDataSnapshot(event.cartData);
        if (!cartSnapshot || cartSnapshot.signature === this.lastCartUpdateSignature) return;
        this.lastCartUpdateSignature = cartSnapshot.signature;
        this.applyCartSnapshot(cartSnapshot);
        this.recalculateCartSuggestion(cartSnapshot.itemCount);
      });
    }

    stopObservingCartUpdates() {
      if (!this.cartUpdateUnsubscriber) return;

      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }

    readCartDataSnapshot(cartData) {
      if (!cartData || typeof cartData !== 'object' || Array.isArray(cartData) || !Array.isArray(cartData.items)) {
        return null;
      }
      if (!Number.isInteger(cartData.item_count) || cartData.item_count < 0) return null;

      const signatureParts = [];
      const distinctProductIds = [];
      const seenProductIds = new Set();
      for (const item of cartData.items) {
        const productId = item?.product_id;
        const variantId = item?.variant_id;
        const quantity = item?.quantity;
        if (!Number.isInteger(productId) || productId <= 0 || !Number.isInteger(variantId) || variantId <= 0 || !Number.isInteger(quantity) || quantity < 1) {
          return null;
        }
        signatureParts.push(`${variantId}:${quantity}`);
        if (!seenProductIds.has(productId)) {
          seenProductIds.add(productId);
          distinctProductIds.push(String(productId));
        }
      }

      return {
        signature: signatureParts.join('|'),
        anchorProductId: distinctProductIds[0] || null,
        alternateAnchorProductId: distinctProductIds[1] || null,
        itemCount: cartData.item_count,
      };
    }

    recalculateCartSuggestion(itemCount) {
      if (!this.isConnected || !this.isCartSurface) return;

      this.abortPendingRequest();
      this.replaceChildren();
      this.variantMatrices = new WeakMap();
      this.hasRequested = false;
      if (itemCount === 0 || !this.hasCartAnchor) {
        this.settleWithoutSuggestion();
        return;
      }
      this.requestSuggestion({ isRecalculation: true });
    }

    /* --- Sustituciones parciales del drawer ------------------------------ */

    observePartialDrawerUpdates() {
      const drawer = this.drawer;
      if (!drawer || this.partialUpdateObserver) return;

      const drawerItems = drawer.querySelector(JR_XSELL_DRAWER_ITEMS_SELECTOR);
      const drawerFooter = drawer.querySelector(JR_XSELL_DRAWER_FOOTER_SELECTOR);
      const observedParents = new Set(
        [drawerItems?.parentElement, drawerFooter?.parentElement].filter(Boolean)
      );

      if (observedParents.size === 0) return;

      this.partialUpdateObserver = new MutationObserver((mutations) => {
        const hasRelevantReplacement = mutations.some((mutation) =>
          [...mutation.removedNodes, ...mutation.addedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches(JR_XSELL_DRAWER_ITEMS_SELECTOR) ||
                node.matches(JR_XSELL_DRAWER_FOOTER_SELECTOR))
          )
        );

        if (hasRelevantReplacement) this.schedulePartialRecalculation();
      });

      observedParents.forEach((parent) => {
        this.partialUpdateObserver.observe(parent, { childList: true });
      });
    }

    stopObservingPartialDrawerUpdates() {
      if (this.partialUpdateObserver) {
        this.partialUpdateObserver.disconnect();
        this.partialUpdateObserver = null;
      }

      if (this.partialUpdateFrame !== null) {
        cancelAnimationFrame(this.partialUpdateFrame);
        this.partialUpdateFrame = null;
      }
    }

    schedulePartialRecalculation() {
      if (this.partialUpdateFrame !== null) return;

      // Dawn sustituye listado y footer consecutivamente. Un frame agrupa las
      // dos mutaciones y da tiempo a que un render completo desconecte esta
      // instancia y cancele el trabajo pendiente.
      this.partialUpdateFrame = requestAnimationFrame(() => {
        this.partialUpdateFrame = null;
        if (!this.isConnected) return;

        const nextCartSnapshot = this.readCartSnapshot();
        if (!nextCartSnapshot || this.matchesCartSnapshot(nextCartSnapshot)) return;

        // Firma y anclas proceden del mismo subárbol SSR ya sustituido y se
        // actualizan juntas antes de iniciar cualquier petición.
        this.applyCartSnapshot(nextCartSnapshot);
        this.recalculateSuggestion();
      });
    }

    readCartSnapshot() {
      const drawer = this.drawer;
      const drawerItems = drawer?.querySelector(JR_XSELL_DRAWER_ITEMS_SELECTOR);
      if (!drawerItems) return null;

      const signatureParts = [];
      const distinctProductIds = [];
      const seenProductIds = new Set();

      for (const cartLine of drawerItems.querySelectorAll(JR_XSELL_CART_LINE_SELECTOR)) {
        const productId = cartLine.getAttribute(JR_XSELL_PRODUCT_ID_ATTR);
        const quantityInput = cartLine.querySelector(JR_XSELL_QUANTITY_INPUT_SELECTOR);
        const variantId = quantityInput?.getAttribute('data-quantity-variant-id');
        const quantity = quantityInput?.value;

        // La fotografía solo es válida si cada línea aporta conjuntamente los
        // tres valores SSR. No se infieren productos desde variantes o URLs.
        if (!productId || !variantId || typeof quantity !== 'string' || quantity === '') {
          return null;
        }

        signatureParts.push(`${variantId}:${quantity}`);

        if (!seenProductIds.has(productId)) {
          seenProductIds.add(productId);
          distinctProductIds.push(productId);
        }
      }

      return {
        signature: signatureParts.join('|'),
        anchorProductId: distinctProductIds[0] || null,
        alternateAnchorProductId: distinctProductIds[1] || null,
      };
    }

    matchesCartSnapshot(cartSnapshot) {
      return (
        cartSnapshot.signature === this.cartSignature &&
        cartSnapshot.anchorProductId ===
          this.getAttribute(JR_XSELL_ANCHOR_PRODUCT_ID_ATTR) &&
        cartSnapshot.alternateAnchorProductId ===
          this.getAttribute(JR_XSELL_ALTERNATE_ANCHOR_PRODUCT_ID_ATTR)
      );
    }

    applyCartSnapshot(cartSnapshot) {
      this.cartSignature = cartSnapshot.signature;
      this.setOptionalAttribute(
        JR_XSELL_ANCHOR_PRODUCT_ID_ATTR,
        cartSnapshot.anchorProductId
      );
      this.setOptionalAttribute(
        JR_XSELL_ALTERNATE_ANCHOR_PRODUCT_ID_ATTR,
        cartSnapshot.alternateAnchorProductId
      );
    }

    setOptionalAttribute(name, value) {
      if (value === null) {
        this.removeAttribute(name);
        return;
      }

      this.setAttribute(name, value);
    }

    recalculateSuggestion() {
      if (!this.isConnected) return;

      this.abortPendingRequest();
      this.replaceChildren();
      this.variantMatrices = new WeakMap();
      this.hasRequested = false;
      this.state = JR_XSELL_STATE.idle;

      // Una actualización con el drawer cerrado invalida la tarjeta, pero no
      // rompe la carga diferida: la nueva cadena espera la próxima apertura.
      if (!this.isSurfaceOpen) {
        this.observeDrawerOpen();
        return;
      }

      this.requestSuggestion({ isRecalculation: true });
    }

    /* --- Ciclo de obtención ----------------------------------------------- */

    async requestSuggestion({ isRecalculation = false } = {}) {
      if (this.hasRequested) return;
      this.hasRequested = true;

      this.state =
        isRecalculation || this.hasShownBefore
          ? JR_XSELL_STATE.recalculating
          : JR_XSELL_STATE.loading;
      this.abortController = new AbortController();

      const recommendationsUrl = this.getAttribute(JR_XSELL_RECOMMENDATIONS_URL_ATTR);
      const anchorProductId = this.getAttribute(JR_XSELL_ANCHOR_PRODUCT_ID_ATTR);
      const alternateAnchorProductId = this.getAttribute(
        JR_XSELL_ALTERNATE_ANCHOR_PRODUCT_ID_ATTR
      );
      const candidateLimit = this.getAttribute(JR_XSELL_CANDIDATE_LIMIT_ATTR);
      const sectionId = this.sectionId;

      // Sin datos SSR suficientes para la vía curada no se inventa una URL, un
      // ancla ni un límite. T028 fija y valida el pool probado de 10; `count`
      // permanece reservado exclusivamente para la cantidad visible.
      if (
        !recommendationsUrl ||
        !anchorProductId ||
        candidateLimit !== JR_XSELL_CURATED_CANDIDATE_LIMIT ||
        !this.hasValidSurfaceConfiguration() ||
        !sectionId
      ) {
        this.settleWithoutSuggestion();
        return;
      }

      const requestController = this.abortController;

      try {
        const primaryOutcome = await this.requestCuratedSuggestion(
          recommendationsUrl,
          anchorProductId,
          candidateLimit,
          sectionId,
          requestController
        );

        if (primaryOutcome.type === JR_XSELL_CURATED_OUTCOME.interrupted) return;
        if (primaryOutcome.type === JR_XSELL_CURATED_OUTCOME.failure) {
          this.settleWithoutSuggestion();
          return;
        }
        if (primaryOutcome.type === JR_XSELL_CURATED_OUTCOME.suggestion) {
          this.applySuggestion(primaryOutcome.sectionResult);
          return;
        }

        let fallbackSignals = primaryOutcome.sectionResult;

        // Solo un `none` curado conforme habilita el único intento alternativo
        // de D11. El ID ya viene de SSR: no se transforma ni se deriva aquí.
        if (alternateAnchorProductId && alternateAnchorProductId !== anchorProductId) {
          const alternateOutcome = await this.requestCuratedSuggestion(
            recommendationsUrl,
            alternateAnchorProductId,
            candidateLimit,
            sectionId,
            requestController
          );

          if (alternateOutcome.type === JR_XSELL_CURATED_OUTCOME.interrupted) return;
          if (alternateOutcome.type === JR_XSELL_CURATED_OUTCOME.failure) {
            this.settleWithoutSuggestion();
            return;
          }
          if (alternateOutcome.type === JR_XSELL_CURATED_OUTCOME.suggestion) {
            this.applySuggestion(alternateOutcome.sectionResult);
            return;
          }

          fallbackSignals = alternateOutcome.sectionResult;
        }

        // Ambos `none` mantienen loading/recalculating; solo el respaldo o el
        // agotamiento completo de la cadena resolverán el estado vivo.
        await this.requestFallbackSuggestion(fallbackSignals, sectionId, requestController);
      } finally {
        if (this.abortController === requestController) {
          this.abortController = null;
        }
      }
    }

    async requestCuratedSuggestion(
      recommendationsUrl,
      productId,
      candidateLimit,
      sectionId,
      requestController
    ) {
      let requestUrl;

      try {
        requestUrl = new URL(recommendationsUrl, window.location.origin);
        requestUrl.searchParams.set('product_id', productId);
        requestUrl.searchParams.set('intent', 'complementary');
        requestUrl.searchParams.set('limit', candidateLimit);
        requestUrl.searchParams.set('section_id', sectionId);
      } catch (error) {
        console.warn('JRXsell: URL de recomendaciones no válida.', error);
        return { type: JR_XSELL_CURATED_OUTCOME.failure };
      }

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: requestController.signal,
        });

        if (requestController.signal.aborted || !this.isConnected) {
          return { type: JR_XSELL_CURATED_OUTCOME.interrupted };
        }

        if (!response.ok) {
          console.warn(`JRXsell: la vía curada respondió con HTTP ${response.status}.`);
          return { type: JR_XSELL_CURATED_OUTCOME.failure };
        }

        if (response.redirected === true) {
          console.warn('JRXsell: la vía curada respondió con una redirección.');
          return { type: JR_XSELL_CURATED_OUTCOME.failure };
        }

        const html = await response.text();

        if (requestController.signal.aborted || !this.isConnected) {
          return { type: JR_XSELL_CURATED_OUTCOME.interrupted };
        }

        const sectionResult = JRXsellCartRecommendations.readSectionResult(html);

        if (!sectionResult) {
          console.warn('JRXsell: respuesta curada de sección no conforme.');
          return { type: JR_XSELL_CURATED_OUTCOME.failure };
        }

        if (sectionResult.origin !== JR_XSELL_ORIGIN.curated || sectionResult.count !== this.count) {
          console.warn('JRXsell: la vía curada devolvió un origen no conforme.');
          return { type: JR_XSELL_CURATED_OUTCOME.failure };
        }

        return {
          type: sectionResult.hasSuggestion
            ? JR_XSELL_CURATED_OUTCOME.suggestion
            : JR_XSELL_CURATED_OUTCOME.none,
          sectionResult: sectionResult,
        };
      } catch (error) {
        if (
          requestController.signal.aborted ||
          error.name === 'AbortError' ||
          !this.isConnected
        ) {
          return { type: JR_XSELL_CURATED_OUTCOME.interrupted };
        }

        console.warn('JRXsell: falló la petición de la vía curada.', error);
        return { type: JR_XSELL_CURATED_OUTCOME.failure };
      }
    }

    async requestFallbackSuggestion(curatedResult, sectionId, requestController) {
      const requestUrl = this.buildFallbackRequestUrl(curatedResult, sectionId);

      // Ausencia o invalidez de base/señales significa que el respaldo no está
      // autorizado para este carrito. No se fabrica perfil ni se hace petición.
      if (!requestUrl) {
        this.settleWithoutSuggestion();
        return;
      }

      try {
        const response = await fetch(requestUrl.toString(), {
          signal: requestController.signal,
        });

        if (requestController.signal.aborted || !this.isConnected) return;

        let responseUrl;
        try {
          responseUrl = new URL(response.url, window.location.origin);
        } catch (error) {
          console.warn('JRXsell: la vía de respaldo devolvió una URL final no válida.', error);
          this.settleWithoutSuggestion();
          return;
        }

        if (response.redirected === true || responseUrl.pathname !== requestUrl.pathname) {
          console.warn('JRXsell: respuesta de respaldo redirigida descartada.');
          this.settleWithoutSuggestion();
          return;
        }

        if (!response.ok) {
          console.warn(`JRXsell: la vía de respaldo respondió con HTTP ${response.status}.`);
          this.settleWithoutSuggestion();
          return;
        }

        const html = await response.text();

        if (requestController.signal.aborted || !this.isConnected) return;

        const sectionResult = JRXsellCartRecommendations.readSectionResult(html);

        if (!sectionResult || sectionResult.origin !== JR_XSELL_ORIGIN.fallback || sectionResult.count !== this.count) {
          console.warn('JRXsell: respuesta de respaldo de sección no conforme.');
          this.settleWithoutSuggestion();
          return;
        }

        if (sectionResult.hasSuggestion) {
          this.applySuggestion(sectionResult);
          return;
        }

        this.settleWithoutSuggestion();
      } catch (error) {
        if (
          requestController.signal.aborted ||
          error.name === 'AbortError' ||
          !this.isConnected
        )
          return;

        console.warn('JRXsell: falló la petición de la vía de respaldo.', error);
        this.settleWithoutSuggestion();
      }
    }

    buildFallbackRequestUrl(curatedResult, sectionId) {
      const collectionBase = this.getAttribute(JR_XSELL_COLLECTION_BASE_ATTR);
      const fallbackPosition = curatedResult.fallbackPosition;
      const fallbackGender = curatedResult.fallbackGender;
      const shopRoot = window.Shopify?.routes?.root;

      if (
        collectionBase !== 'all' ||
        !JR_XSELL_FALLBACK_POSITIONS.has(fallbackPosition) ||
        !JR_XSELL_FALLBACK_GENDERS.has(fallbackGender) ||
        typeof shopRoot !== 'string' ||
        shopRoot.length === 0
      )
        return null;

      try {
        const localizedRoot = new URL(shopRoot, window.location.origin);
        const rootPath = localizedRoot.pathname.endsWith('/')
          ? localizedRoot.pathname
          : `${localizedRoot.pathname}/`;
        const requestUrl = new URL(
          `${rootPath}collections/${collectionBase}/${fallbackPosition}+${fallbackGender}`,
          window.location.origin
        );

        requestUrl.searchParams.set('sort_by', 'price-ascending');
        requestUrl.searchParams.set('section_id', sectionId);
        return requestUrl;
      } catch (error) {
        console.warn('JRXsell: URL de respaldo no válida.', error);
        return null;
      }
    }

    /* --- Contrato de lectura sección → JS -------------------------------- */

    /**
     * Lee el resultado de una respuesta de sección usando EXCLUSIVAMENTE los
     * atributos de su elemento raíz. NUNCA inspecciona el contenido interno
     * para deducir el estado: esa es la regla central del contrato, porque el
     * contenido puede cambiar (tarjeta, selector, mensajes) sin que cambie el
     * significado, y deducirlo del HTML acoplaría el JS al markup.
     *
     * @param {string} html  Cuerpo de la respuesta de Section Rendering API.
     * @returns {{origin: string, result: string, hasSuggestion: boolean, markup: string, fallbackPosition: string|null, fallbackGender: string|null}|null}
     *          `null` si la respuesta no trae un raíz conforme al contrato.
     */
    static readSectionResult(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const root = parsed.querySelector(JR_XSELL_ELEMENT_NAME);

      // Respuesta no conforme: no se adivina nada a partir del contenido.
      if (!root) return null;

      const result = root.getAttribute(JR_XSELL_RESULT_ATTR);
      const isKnownResult = result === JR_XSELL_RESULT.suggestion || result === JR_XSELL_RESULT.none;

      if (!isKnownResult) return null;

      return {
        origin: root.getAttribute(JR_XSELL_ORIGIN_ATTR),
        result: result,
        count: root.getAttribute('data-jr-xsell-count'),
        hasSuggestion: result === JR_XSELL_RESULT.suggestion,
        markup: root.innerHTML,
        fallbackPosition: root.getAttribute(JR_XSELL_FALLBACK_POSITION_ATTR),
        fallbackGender: root.getAttribute(JR_XSELL_FALLBACK_GENDER_ATTR),
      };
    }

    /**
     * Aplica una respuesta de sección que SÍ trae sugerencia: vuelca su
     * contenido, pasa a `ready` y marca el ancestro para que las recreaciones
     * posteriores reserven la altura desde el primer paint.
     *
     * El caso contrario (`result === 'none'`) NO se resuelve aquí: lo decide la
     * orquestación de fuentes según queden o no fuentes por consultar — ver la
     * tabla de decisión en contracts/module-component.md. Escalar es
     * responsabilidad de T017b/T025/T029; agotar la cadena termina en
     * `settleWithoutSuggestion()`.
     */
    applySuggestion(sectionResult) {
      this.innerHTML = sectionResult.markup;
      this.hydrateVariantPickers();
      this.state = JR_XSELL_STATE.ready;
      this.markSuggestionShown();
      this.persistReservedHeight();
    }

    /** Cadena de fuentes agotada: colapsa sin hueco ni mensaje (FR-003). */
    settleWithoutSuggestion() {
      this.state = JR_XSELL_STATE.empty;
    }

    abortPendingRequest() {
      if (!this.abortController) return;

      this.abortController.abort();
      this.abortController = null;
    }

    /* --- Estado ------------------------------------------------------------ */

    get state() {
      return this.getAttribute(JR_XSELL_STATE_ATTR) || JR_XSELL_STATE.idle;
    }

    set state(value) {
      this.setAttribute(JR_XSELL_STATE_ATTR, value);

      const isBusy = value === JR_XSELL_STATE.loading || value === JR_XSELL_STATE.recalculating;
      this.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    /**
     * Marca en el ANCESTRO que esta superficie ya mostró una sugerencia, para que
     * las recreaciones posteriores reserven la altura desde el primer paint.
     *
     * Lo llama `applySuggestion()` cuando cualquier fuente conforme devuelve
     * `result="suggestion"`.
     */
    markSuggestionShown() {
      const drawer = this.drawer;
      if (!drawer) return;

      drawer.setAttribute(JR_XSELL_SHOWN_ATTR, 'true');
    }

    /**
     * Eleva en `<cart-drawer>` la reserva al mayor alto real ya mostrado. El
     * valor nunca disminuye durante la vida del drawer y queda disponible para
     * el primer paint de cualquier instancia recreada.
     */
    persistReservedHeight() {
      const drawer = this.drawer;
      if (!drawer || !this.isConnected) return;

      const measuredHeight = this.getBoundingClientRect().height;
      if (!Number.isFinite(measuredHeight) || measuredHeight <= 0) return;

      const storedValue = drawer.style.getPropertyValue(JR_XSELL_RESERVED_HEIGHT_PROPERTY).trim();
      const storedMatch = storedValue.match(/^(\d+(?:\.\d+)?)px$/);
      const parsedStoredHeight = storedMatch ? Number(storedMatch[1]) : 0;
      const storedHeight =
        Number.isFinite(parsedStoredHeight) && parsedStoredHeight > 0 ? Math.ceil(parsedStoredHeight) : 0;
      const reservedHeight = Math.max(storedHeight, Math.ceil(measuredHeight));

      if (reservedHeight > storedHeight) {
        drawer.style.setProperty(JR_XSELL_RESERVED_HEIGHT_PROPERTY, `${reservedHeight}px`);
      }
    }
  }

  if (!customElements.get(JR_XSELL_ELEMENT_NAME)) {
    customElements.define(JR_XSELL_ELEMENT_NAME, JRXsellCartRecommendations);
  }
})();
